/**
 * Admin · Review Follow-up Rollout Presets (Phase 2L-A83)
 *
 * Two scoped feature-flag presets:
 *   1. Apply Review Follow-Up Rollout Preset — flips the 8
 *      review_referral flags into the locked production-ready
 *      matrix. Idempotent.
 *   2. Emergency Safe Mode — turns all 8 review_referral flags OFF.
 *
 * Safety rails:
 *   • Two-step confirm modal — never one click.
 *   • Diff (before/after) is shown BEFORE the user can confirm.
 *   • No-op runs are short-circuited with a friendly toast.
 *   • Admin-only at API + UI (the FeatureFlags page is already
 *     admin-only via the route gate; this component lives only
 *     inside that page).
 *   • Does NOT delete data, redeem vouchers, alter Stripe records,
 *     or touch invite/voucher audit history.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Rocket, ShieldAlert, X, ArrowRight, CheckCircle2, Loader2,
  AlertTriangle, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

const STATUS_TONE = {
  enabled:       "text-emerald-700 bg-emerald-50 border-emerald-200",
  internal_only: "text-amber-700 bg-amber-50 border-amber-200",
  disabled:      "text-rose-700 bg-rose-50 border-rose-200",
};

const STATUS_LABEL = {
  enabled: "ON",
  internal_only: "Internal",
  disabled: "OFF",
};

function StatusPill({ status, testid }) {
  const cls = STATUS_TONE[status] || STATUS_TONE.disabled;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${cls}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export function ReviewReferralRolloutPresets({ onApplied }) {
  const [diffs, setDiffs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // "rollout_prod" | "emergency_safe_mode" | null
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/review-referral-presets");
      setDiffs(data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (preset) => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/review-referral-presets/apply", {
        preset,
        reason: "Admin UI rollout preset",
      });
      if (data.is_noop) {
        toast.success("All flags already match this preset — no changes needed");
      } else {
        toast.success(`Applied — ${data.total_changed} flag(s) changed`);
      }
      setModal(null);
      await load();
      if (onApplied) await onApplied();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  const active = modal && diffs ? diffs[modal] : null;

  return (
    <section
      data-testid="rollout-presets-section"
      className="mt-6 rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50/60 to-white p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-cyan-700 mb-1">
            Review Follow-up · rollout controls
          </div>
          <h2 className="text-xl font-bold text-slate-900">
            Apply rollout preset or activate Emergency Safe Mode
          </h2>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            Two scoped controls that flip ONLY the 8{" "}
            <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
              review_referral
            </code>{" "}
            flags. Every action shows a before / after diff and requires
            explicit confirmation. Never touches data, vouchers, Stripe,
            bookings, or audit history — only flag rows.
          </p>
        </div>
        <button
          type="button"
          data-testid="rollout-presets-refresh"
          onClick={load}
          disabled={loading}
          title="Refresh diff"
          className="p-1.5 rounded hover:bg-white text-slate-500 hover:text-slate-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm px-4 py-3">
          <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          {error}
        </div>
      )}

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <PresetCard
          tone="cyan"
          icon={Rocket}
          title="Apply Review Follow-Up Rollout Preset"
          blurb="Turn the core Review Follow-Up + Invite-a-Neighbour + Voucher flow ON. Membership offer cards stay OFF until copy review."
          diff={diffs?.rollout_prod}
          loading={loading}
          onClick={() => setModal("rollout_prod")}
          testId="rollout-preset-prod-btn"
        />
        <PresetCard
          tone="rose"
          icon={ShieldAlert}
          title="Emergency Safe Mode"
          blurb="Turn ALL 8 review/referral/voucher flags OFF in one controlled action. Does not delete records, unwind redeemed vouchers, cancel bookings, or alter Stripe."
          diff={diffs?.emergency_safe_mode}
          loading={loading}
          onClick={() => setModal("emergency_safe_mode")}
          testId="emergency-safe-mode-btn"
        />
      </div>

      {modal && active && (
        <ConfirmModal
          preset={modal}
          diff={active}
          applying={applying}
          onCancel={() => !applying && setModal(null)}
          onConfirm={() => apply(modal)}
        />
      )}
    </section>
  );
}


function PresetCard({ tone, icon: Icon, title, blurb, diff, loading, onClick, testId }) {
  const willChange = diff?.will_change ?? 0;
  const isNoop = !!diff?.is_noop;
  const toneRing = tone === "rose" ? "border-rose-200" : "border-cyan-200";
  const toneBtn  = tone === "rose"
    ? "bg-rose-600 hover:bg-rose-700"
    : "bg-cyan-600 hover:bg-cyan-700";

  return (
    <div className={`rounded-2xl bg-white border ${toneRing} p-5 flex flex-col`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-2 rounded-xl ${tone === "rose" ? "bg-rose-50 text-rose-600" : "bg-cyan-50 text-cyan-600"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 leading-tight">{title}</div>
          <p className="text-xs text-slate-500 leading-relaxed mt-1">{blurb}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500">
          {loading ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Calculating diff…
            </span>
          ) : isNoop ? (
            <span data-testid={`${testId}-noop`} className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="w-3 h-3" /> Already matches
            </span>
          ) : (
            <span data-testid={`${testId}-will-change`}>
              {willChange} of {diff?.total ?? 8} flag{(willChange === 1) ? "" : "s"} will change
            </span>
          )}
        </div>
        <Button
          onClick={onClick}
          data-testid={testId}
          disabled={loading}
          className={`rounded-full text-white font-semibold ${toneBtn}`}
        >
          Review &amp; apply
        </Button>
      </div>
    </div>
  );
}


function ConfirmModal({ preset, diff, applying, onCancel, onConfirm }) {
  const isEmergency = preset === "emergency_safe_mode";
  const headerTone = isEmergency ? "bg-rose-50 border-rose-200" : "bg-cyan-50 border-cyan-200";
  const confirmBtn = isEmergency
    ? "bg-rose-600 hover:bg-rose-700"
    : "bg-cyan-600 hover:bg-cyan-700";

  return (
    <div
      data-testid="rollout-preset-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className={`px-6 py-4 border-b ${headerTone} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            {isEmergency
              ? <ShieldAlert className="w-5 h-5 text-rose-600" />
              : <Rocket className="w-5 h-5 text-cyan-600" />}
            <h3 className="font-display font-bold text-lg text-slate-900">
              {diff.label}
            </h3>
          </div>
          <button
            type="button"
            data-testid="rollout-preset-modal-close"
            onClick={onCancel}
            disabled={applying}
            className="p-1 rounded hover:bg-white/50 text-slate-500 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {isEmergency ? (
            <div className="mb-4 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800">
              <strong>This is a kill-switch.</strong> All review-followup
              emails, invites, voucher generation, and voucher
              redemption at booking checkout will stop. Existing
              redeemed vouchers, paid bookings, sent emails, and audit
              history are <strong>NOT</strong> affected.
            </div>
          ) : (
            <div className="mb-4 rounded-xl bg-cyan-50 border border-cyan-200 p-4 text-sm text-slate-700">
              This will turn the core Review Follow-Up +
              Invite-a-Neighbour + Voucher flow ON. The Membership
              offer card stays OFF — flip those individually
              after copy review.
            </div>
          )}

          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            Before / after
          </div>
          <ul data-testid="rollout-preset-diff-list" className="space-y-2">
            {diff.rows.map((row) => (
              <li
                key={row.key}
                data-testid={`rollout-preset-diff-row-${row.key}`}
                className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${row.changes ? "border-slate-300 bg-white" : "border-slate-100 bg-slate-50/40"}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {row.name}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400 truncate">
                    {row.key}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusPill status={row.current} testid={`rollout-preset-current-${row.key}`} />
                  <ArrowRight className={`w-3.5 h-3.5 ${row.changes ? "text-slate-700" : "text-slate-300"}`} />
                  <StatusPill status={row.target} testid={`rollout-preset-target-${row.key}`} />
                  {row.changes && (
                    <span
                      data-testid={`rollout-preset-row-change-${row.key}`}
                      className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-700"
                    >
                      change
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 text-xs text-slate-500">
            {diff.will_change === 0
              ? "No changes will be made — every flag already matches this preset."
              : `${diff.will_change} of ${diff.total} flags will change. The rest are already at the target state.`}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500 flex-1">
            Logged to the feature-flag audit trail with your admin account.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              data-testid="rollout-preset-cancel-btn"
              onClick={onCancel}
              disabled={applying}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="rollout-preset-confirm-btn"
              onClick={onConfirm}
              disabled={applying}
              className={`rounded-full text-white font-semibold ${confirmBtn}`}
            >
              {applying
                ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Applying…</>)
                : (diff.will_change === 0 ? "Confirm (no-op)" : `Confirm — change ${diff.will_change} flag${diff.will_change === 1 ? "" : "s"}`)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
